import { useQuery } from '@tanstack/react-query'
import { analyticsEvent } from 'global/helpers/analytics'
import { appHistory } from 'global/helpers/history'
import { isServerError } from 'global/helpers/requestError'
import i18n from 'i18n'
import { createContext, type ReactElement, useCallback, useMemo, useState } from 'react'
import { storeLanguage, userPreferencesQueryOptions } from 'screens/UserManager/UserManager.service'
import { AuthImpl } from './Auth.service'
import type { Auth } from './Auth.types'
import { getUserCompanies } from './Company.service'
import { clearSelectedCompany, getSelectedCompany, setSelectedCompany } from './User.helpers'
import type { KNUser, UserContextProps } from './UserContext.types'

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const UserContext = createContext<UserContextProps>({} as UserContextProps)

export const UserProvider = ({ children }): ReactElement => {
  const auth: Auth = useMemo(() => new AuthImpl(), [])
  const [user, setUser] = useState<KNUser>()
  const [userError, setUserError] = useState<string>()
  const [userLoading, setUserLoading] = useState(true)
  const [userResetLoading, setUserResetLoading] = useState(false)
  const [userResetResult, setUserResetResult] = useState('')
  const userPreferencesQuery = useQuery({
    ...userPreferencesQueryOptions(),
    enabled: !!user,
  })

  const logoutUser = useCallback((): void => {
    auth.signOut()
    clearSelectedCompany()
    analyticsEvent('polestar_user_logged_out')
    window.location.replace('/login')
  }, [auth])

  const loadUser = useCallback(async (): Promise<void> => {
    setUserLoading(true)
    try {
      const queryParams = new URLSearchParams(appHistory.location.search)
      const loginRedirect = queryParams.get('redirect')
      const loginCompanyCid = queryParams.get('company')
      const user = await auth.getCurrentUser()
      if (user) {
        const companies = await getUserCompanies(user.idToken)
        setUser({
          ...user,
          companies: companies,
          type: companies.some((value) => value.cid === 'zb2rhXuNLamWn2NzucL623EfT9871AWq1Bv5FL7ij3AyguuaF')
            ? 'Admin'
            : 'User',
        })
        if (companies.length > 0 && !getSelectedCompany()) {
          if (loginCompanyCid) {
            setSelectedCompany(companies.find((company) => company.cid === loginCompanyCid) ?? companies[0]!)
          } else {
            setSelectedCompany(companies[0]!)
          }
        }
        const storedLanguage = localStorage.getItem('userLanguage')
        const currentLanguage = storedLanguage ?? user.language ?? i18n.language
        if (i18n.language !== currentLanguage) {
          await i18n.changeLanguage(currentLanguage)
        }
        if (storedLanguage !== currentLanguage) {
          await storeLanguage({
            language: currentLanguage,
          })
          localStorage.setItem('userLanguage', currentLanguage)
        }
      }
      if (user && loginRedirect?.startsWith('/') && !loginRedirect.startsWith('//')) {
        appHistory.replace(loginRedirect)
      }
    } catch {
      auth.signOut()
    }
    setUserLoading(false)
  }, [auth])

  const loginUser = useCallback(async (email: string, password: string): Promise<void> => {
    setUserLoading(true)
    setUserError(undefined)
    clearSelectedCompany()
    try {
      await auth.signInWithEmailAndPassword(email, password)
      await loadUser()
      analyticsEvent('polestar_user_logged_in')
    } catch (error) {
      if (isServerError(error) && error.response.status === 401) {
        setUserError(i18n.t('errors.LoginFailed'))
      } else {
        setUserError(i18n.t('errors.GenericError'))
      }
    }
    setUserLoading(false)
  }, [auth, loadUser])

  const resetUser = useCallback(async (email: string): Promise<void> => {
    setUserResetLoading(true)
    try {
      await auth.sendPasswordResetEmail(email)
      setUserResetResult('success')
    } catch {
      setUserResetResult('error')
    }
    setUserResetLoading(false)
  }, [auth])

  const verifyPasswordResetCode = useCallback(async (oobCode: string): Promise<void> => {
    setUserResetLoading(true)
    try {
      await auth.verifyPasswordResetCode(oobCode)
    } finally {
      setUserResetLoading(false)
    }
  }, [auth])

  const confirmPasswordReset = useCallback(async (oobCode: string, newPassword: string): Promise<void> => {
    setUserResetLoading(true)
    try {
      await auth.confirmPasswordReset(oobCode, newPassword)
    } finally {
      setUserResetLoading(false)
    }
  }, [auth])

  const userContextValue = useMemo(() => ({
    user,
    userLoading,
    userError,
    userPreferences: userPreferencesQuery.data ?? {},
    logoutUser,
    loadUser,
    loginUser,
    resetUser,
    verifyPasswordResetCode,
    confirmPasswordReset,
    userResetLoading,
    userResetResult,
  }), [
    user,
    userLoading,
    userError,
    userPreferencesQuery.data,
    logoutUser,
    loadUser,
    loginUser,
    resetUser,
    verifyPasswordResetCode,
    confirmPasswordReset,
    userResetLoading,
    userResetResult,
  ])

  return (
    <UserContext.Provider value={userContextValue}>
      {children}
    </UserContext.Provider>
  )
}
